/**
 * Truncates a filename to show the beginning and end, preserving the file extension.
 * This creates the familiar OS-style truncation pattern: "really-long-name...end-1.pdf"
 */
export const truncateFilename = (filename: string, maxLength: number = 30, keepExtension: boolean = true): string => {
	if (filename.length <= maxLength) return filename;

	if (keepExtension) {
		// Separate name and extension
		const lastDotIndex = filename.lastIndexOf('.');
		const name = lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
		const extension = lastDotIndex > 0 ? filename.substring(lastDotIndex) : '';

		// Calculate how many characters we can show from the name
		const availableLength = maxLength - extension.length - 3; // 3 for "..."

		if (availableLength <= 10) {
			// Not enough space, just truncate normally
			return filename.substring(0, maxLength - 3) + '...';
		}

		// Show start and end of name, keep full extension
		const startLength = Math.ceil(availableLength * 0.6); // 60% for start
		const endLength = Math.floor(availableLength * 0.4); // 40% for end

		return name.substring(0, startLength) + '...' + name.substring(name.length - endLength) + extension;
	}

	// Without extension preservation
	const startLength = Math.ceil(maxLength * 0.6);
	const endLength = Math.floor(maxLength * 0.4) - 3;

	return filename.substring(0, startLength) + '...' + filename.substring(filename.length - endLength);
};

/**
 * Decimal, so a size reads the same here as it does in Finder and in the backend's admin panel.
 *
 * Dividing by 1024 and labelling the result KB/MB/GB is the common mistake — those are KiB/MiB/GiB,
 * and the error compounds: 2.4% at KB, 4.9% at MB, 7.4% at GB, so a 12 MB file shows as 11.4 MB and
 * nobody can reconcile it with the file on disk.
 *
 * This is for reporting a size that was *observed*. A ceiling defined in binary units is a different
 * case and is right to render in them — the upload limit is set from a number of MiB, and its message
 * says so. Do not unify the two.
 */
const BYTES_PER_UNIT = 1000;
const SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;

/**
 * A byte count as something scannable.
 *
 * Empty rather than `0 B` when there is no size to report: records predating the column carry none,
 * and a zero would read as an empty file.
 */
export const formatFileSize = (sizeBytes: number | null | undefined): string => {
	if (sizeBytes === null || sizeBytes === undefined) return '';

	let size = sizeBytes;
	let unitIndex = 0;
	while (size >= BYTES_PER_UNIT && unitIndex < SIZE_UNITS.length - 1) {
		size /= BYTES_PER_UNIT;
		unitIndex += 1;
	}

	const unit = SIZE_UNITS[unitIndex];
	// Whole bytes only below a kilobyte: "512 B" is exact, where "0.5 KB" is the same number said
	// less precisely.
	return unit === 'B' ? `${Math.round(size)} ${unit}` : `${size.toFixed(1)} ${unit}`;
};
